/**
 * useUserIdentity Hook
 * 
 * Single source of truth for user identity across the application.
 * Provides consistent access to user data, identity utilities, and UI helpers.
 * 
 * This is the primary hook that all components should use for user-related data.
 * It consolidates data from multiple sources into a clean, consistent API.
 */

import { useCurrentUser } from './useCurrentUser';
import { useAppSelector } from '../../store/hooks';
import { selectUserSession } from '../../store/slices/userProfileSlice';
import { useMemo } from 'react';

export interface UserIdentity {
  // Core identity utilities
  isCurrentUser: (senderId: string | null | undefined) => boolean;
  userId: string | null; // Stytch/Auth ID
  apiUserId: string | null; // Backend API ID
  
  // User profile data
  profile: {
    id: string | null;
    email?: string;
    fullName: string;
    initials: string;
    profileImageUrl?: string | null; // Expose the actual profile image URL
    avatarColor?: string | null; // Expose the avatar color
    avatar: {
      type: 'image' | 'text';
      value: string;
      initials: string;
    };
    organizationId: string | null;
    role?: string;
    isOnline: boolean;
  } | null;
  
  // UI helpers
  displayName: string;
  avatarData: {
    type: 'image' | 'text';
    value: string;
    initials: string;
  };
  profileImageUrl?: string | null; // Direct access to profile image URL
  avatarColor?: string | null; // Direct access to avatar color
  
  // Message-specific helpers
  isMyMessage: (senderId: string | null | undefined) => boolean;
  
  // Loading state
  isLoading: boolean;
}

/**
 * Universal hook for user identity throughout the app
 * 
 * @returns UserIdentity object with all user-related data and utilities
 */
export function useUserIdentity(): UserIdentity {
  // Get data from existing hooks and Redux
  const { userId, apiUserId, isCurrentUser: isCurrentUserUtil, isLoading } = useCurrentUser();
  const userSession = useAppSelector(selectUserSession);
  
  // Memoize the identity object to prevent unnecessary re-renders
  return useMemo(() => {
    // Create consistent profile object
    const profile = userSession ? {
      id: userSession.id,
      email: userSession.email,
      fullName: userSession.fullName,
      initials: userSession.initials,
      profileImageUrl: userSession.profileImageUrl || null,
      avatarColor: userSession.avatarColor || null,
      avatar: userSession.avatar,
      organizationId: userSession.organizationId || null,
      role: userSession.role,
      isOnline: userSession.isOnline ?? true,
    } : null;
    
    // Default avatar for when no profile is available
    const defaultAvatar = {
      type: 'text' as const,
      value: '#3B82F6',
      initials: 'U'
    };
    
    return {
      // Core identity utilities
      isCurrentUser: isCurrentUserUtil,
      userId,
      apiUserId,
      
      // Profile data
      profile,
      
      // UI helpers
      displayName: profile?.fullName || 'User',
      avatarData: profile?.avatar || defaultAvatar,
      profileImageUrl: profile?.profileImageUrl || null,
      avatarColor: profile?.avatarColor || null,
      
      // Message-specific helpers
      isMyMessage: isCurrentUserUtil, // Alias for clarity in messaging contexts
      
      // Loading state
      isLoading,
    };
  }, [userId, apiUserId, isCurrentUserUtil, userSession, isLoading]);
}

/**
 * Hook for components that only need to check if a message is from current user
 * Optimized for messaging components
 */
export function useIsCurrentUser() {
  const { isCurrentUser } = useUserIdentity();
  return isCurrentUser;
}

/**
 * Hook for components that only need user display data
 * Optimized for UI components like avatars and headers
 */
export function useUserDisplay() {
  const { displayName, avatarData, profile } = useUserIdentity();
  return { displayName, avatarData, profile };
}